import { createFileRoute } from '@tanstack/react-router'
import { Badge } from '@workspace/ui/components/badge'
import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import { ProfileSection } from '@workspace/ui/components/profile-section'
import { ArrowDownLeft, ArrowUpRight, Coins, Wallet } from 'lucide-react'
import {
  useInvestorCreditsQuery,
  useInvestorQuery,
  useStartupCreditsQuery,
  useStartupQuery,
} from '@/hooks/api'
import { useIsFounder, useIsInvestor } from '@/hooks/use-user-type'
import { formatFullDate } from '@/utils/format'

export const Route = createFileRoute('/_protected/credits')({
  component: CreditsPage,
})

function CreditsPage() {
  const isFounder = useIsFounder()
  const isInvestor = useIsInvestor()

  const { data: startupData } = useStartupQuery({ enabled: isFounder })
  const { data: investorData } = useInvestorQuery({ enabled: isInvestor })
  const startup = startupData?.data
  const investor = investorData?.data

  const startupCredits = useStartupCreditsQuery(isFounder ? startup?.id : undefined)
  const investorCredits = useInvestorCreditsQuery(isInvestor ? investor?.id : undefined)

  const credits = isFounder ? startupCredits : investorCredits
  const balance = credits.data?.data.balance
  const transactions = credits.data?.data.transactions

  return (
    <div className="space-y-8 py-8">
      {/* Balance Card */}
      <ProfileSection title="Balance">
        <div className="rounded-2xl border border-border bg-input/50 p-5">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center size-12 rounded-xl bg-elevated">
              <Wallet className="size-6 text-[#f97316]" />
            </div>
            <div>
              {balance !== undefined ? (
                <>
                  <p className="text-3xl font-bold text-white tabular-nums">{balance}</p>
                  <p className="text-xs text-muted-foreground">Credits available</p>
                </>
              ) : (
                <LoadingSpinner size="sm" />
              )}
            </div>
          </div>
        </div>
      </ProfileSection>

      {/* Transaction History */}
      <ProfileSection title="Transaction History">
        {credits.isLoading ? (
          <div className="flex items-center justify-center py-16">
            <LoadingSpinner size="md" />
          </div>
        ) : transactions && transactions.length > 0 ? (
          <div className="rounded-2xl border border-border bg-input/50 overflow-hidden">
            <div className="divide-y divide-border">
              {transactions.map((tx) => {
                const isGrant = tx.amount > 0
                return (
                  <div
                    key={tx.id}
                    className="flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-secondary/30"
                  >
                    <div
                      className={`flex items-center justify-center size-8 rounded-full shrink-0 ${isGrant ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}
                    >
                      {isGrant ? (
                        <ArrowDownLeft className="size-4 text-emerald-400" />
                      ) : (
                        <ArrowUpRight className="size-4 text-red-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-white">
                          {tx.type === 'ADMIN_TOPUP' ? 'Credit Grant' : 'Usage'}
                        </p>
                        <Badge variant={isGrant ? 'success' : 'secondary'} size="sm">
                          {tx.type === 'ADMIN_TOPUP' ? 'Grant' : 'Usage'}
                        </Badge>
                      </div>
                      {tx.note && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{tx.note}</p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p
                        className={`text-sm font-semibold tabular-nums ${isGrant ? 'text-emerald-400' : 'text-red-400'}`}
                      >
                        {isGrant ? `+${tx.amount}` : tx.amount}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatFullDate(tx.createdAt)}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-border bg-input/50 p-12 flex flex-col items-center text-center">
            <div className="flex items-center justify-center size-16 rounded-2xl bg-elevated mb-5">
              <Coins className="size-7 text-muted-foreground" />
            </div>
            <h2 className="text-lg font-semibold text-white mb-1">No transactions yet</h2>
            <p className="text-sm text-muted-foreground max-w-sm">
              Your credit transactions will appear here
            </p>
          </div>
        )}
      </ProfileSection>
    </div>
  )
}
